import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef } from "preact";
import { passkeyResponse } from "~/backend/validate.js";
import { ApiError, query, authedApiRequest, response } from "~/lib/api.js";
import { register } from "~/lib/passkey.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import { array, object, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Dropdown from "~/component/Dropdown.js";
import Field from "~/component/Field.js";
import Icon from "~/component/Icon.js";
import Modal from "~/component/Modal.js";
import StepUp from "~/component/StepUp.js";
import Time from "~/component/Time.js";
import button from "~/style/button.js";
import input from "~/style/input.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Passkeys.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Passkeys.module.css", import.meta.url);
const css = adopt(sheet);

/** @type {Record<string, string>} */
const PROVIDERS = {
  "1password": "1Password",
  bitwarden: "Bitwarden",
  chrome_on_mac: "Chrome on Mac",
  chromium_browser: "Chromium Browser",
  dashlane: "Dashlane",
  devolutions: "Devolutions",
  edge_on_mac: "Edge on Mac",
  enpass: "Enpass",
  google_password_manager: "Google Password Manager",
  icloud_keychain: "iCloud Keychain",
  icloud_keychain_managed: "iCloud Keychain (Managed)",
  idmelon: "IDmelon",
  ipasswords: "iPasswords",
  kaspersky_password_manager: "Kaspersky Password Manager",
  keepassxc: "KeePassXC",
  keeper: "Keeper",
  lastpass: "LastPass",
  logmeonce: "LogMeOnce",
  nordpass: "NordPass",
  proton_pass: "Proton Pass",
  samsung_pass: "Samsung Pass",
  sesame: "Sésame",
  thales_bio_ios: "Thales Bio (iOS)",
  thales_bio_android: "Thales Bio (Android)",
  thales_pin_android: "Thales PIN (Android)",
  thales_pin_ios: "Thales PIN (iOS)",
  toothpic: "ToothPic",
  windows_hello: "Windows Hello",
  wikey_security: "Wikey Security",
  yubikey: "Yubikey",
  zoho_vault: "Zoho Vault"
};

/** @param {passkeyResponse} passkey */
function passkeyName(passkey) {
  if (passkey.name) return passkey.name;
  const type = PROVIDERS[passkey.key_type || ""];
  if (type) return type;
  return "Untitled passkey";
}

/** @extends {ApiError<"name">} */
class PasskeyError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        invalid_credentials: "Your credentials are incorrect.",
        invalid_token: "Your verification has expired. Please try again.",
        tfa_required: "A two-factor authentication code is required.",
        passkey_error: "Couldn't find passkey.",
        no_credential: "Couldn't find passkey."
      },
      name: {
        "validation_failed.name": "Name must be 2–100 characters and only contain letters, numbers and spaces."
      }
    };
  }
}
export default class Passkeys extends Component {
  passkeys = query(() => ({
    key: ["/v2/passkeys"]
  }), {
    schema: response(array(passkeyResponse))
  });
  adding = signal(false);

  /** @type {Signal<passkeyResponse | undefined>} */
  editing = signal(undefined);

  /** @type {Signal<passkeyResponse | undefined>} */
  deleting = signal(undefined);
  render() {
    return [__h("div", {
      "class": css.add
    }, __h("p", {
      "class": css.blurb
    }, "Passkeys are an easier and more secure way to log into your account."), __h(Button, {
      onClick: () => {
        this.adding.value = true;
      }
    }, "Add passkey")), __h("ul", {
      "class": css.passkeys
    }, this.passkeys.data?.data.map(passkey => __h("li", {
      key: passkey.id,
      "class": css.passkey
    }, __h("div", {
      "class": css.passkeyInfo
    }, __h("span", {
      "class": css.passkeyName
    }, " ", passkeyName(passkey), " "), __h("span", {
      "class": css.passkeyUsed
    }, passkey.last_used_at ? ["Last used on ", __h(Time, {
      datetime: new Date(passkey.last_used_at)
    })] : "Never used")), __h(Dropdown, {
      id: "passkey-options-" + passkey.id,
      placement: "bottom-end",
      trigger: [" ", __h("button", {
        type: "button",
        "class": button.button,
        popovertarget: "passkey-options-" + passkey.id,
        "data-level": "tertiary",
        "data-size": "small"
      }, __h(Icon, {
        name: "menu",
        size: 12,
        label: "menu"
      }))],
      items: [{
        text: "Rename",
        icon: "pencil",
        onSelect: () => {
          this.editing.value = passkey;
        }
      }, {
        text: "Remove",
        icon: "trash",
        onSelect: () => {
          this.deleting.value = passkey;
        }
      }]
    })))), __h(AddPasskeyModal, {
      open: this.adding.value,
      onAdd: () => {
        this.adding.value = false;
        this.passkeys.fetch();
      },
      onClose: () => {
        this.adding.value = false;
      }
    }), __h(EditPasskeyModal, {
      passkey: this.editing.value,
      onEdit: () => {
        this.editing.value = undefined;
        this.passkeys.fetch();
      },
      onClose: () => {
        this.editing.value = undefined;
      }
    }), __h(DeletePasskeyModal, {
      passkey: this.deleting.value,
      onDelete: () => {
        this.deleting.value = undefined;
        this.passkeys.fetch();
      },
      onClose: () => {
        this.deleting.value = undefined;
      }
    })];
  }
}

/**
 * @typedef {object} AddPasskeyModalProps
 * @property {boolean} open
 * @property {() => void} onAdd
 * @property {() => void} onClose
 */

/** @extends Component<AddPasskeyModalProps> */
class AddPasskeyModal extends Component {
  #stepUpRef = (/** @type {{ current: StepUp }} */createRef());
  addPasskey = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    const form = ev.currentTarget;
    const token = await this.#stepUpRef.current.requestToken("passkey_register");
    await register(token);
    form.reset();
    this.props.onAdd();
  });
  render() {
    const {
      open,
      onClose
    } = this.props;
    const error = this.addPasskey.error && PasskeyError.from(this.addPasskey.error);
    return __h(Modal, {
      title: "Add passkey",
      open: open,
      onClose: onClose
    }, __h("p", {}, "Verify your identity to add a passkey to your account."), __h("form", {
      "class": css.form,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        this.addPasskey.invoke(ev);
      }
    }, __h(StepUp, {
      ref: this.#stepUpRef,
      error: error
    }), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.addPasskey.loading
    }, "Add passkey"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      disabled: this.addPasskey.loading
    }, "Cancel"))));
  }
}

/**
 * @typedef {object} EditPasskeyModalProps
 * @property {passkeyResponse | undefined} [passkey]
 * @property {() => void} onEdit
 * @property {() => void} onClose
 */

/** @extends Component<EditPasskeyModalProps> */
class EditPasskeyModal extends Component {
  editPasskey = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    if (!this.props.passkey) return;
    const form = new FormData(ev.currentTarget);
    const data = parse(object({
      name: string
    }), Object.fromEntries(form.entries()));
    await authedApiRequest("PATCH", `/v2/passkeys/${this.props.passkey.id}`, {
      json: {
        name: data.name
      }
    });
    this.props.onEdit();
  });
  render() {
    const {
      passkey,
      onClose
    } = this.props;
    const error = this.editPasskey.error && PasskeyError.from(this.editPasskey.error);
    return __h(Modal, {
      title: "Edit passkey",
      open: passkey !== undefined,
      onClose: onClose
    }, __h("form", {
      "class": css.form,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        this.editPasskey.invoke(ev);
      }
    }, __h(Field, {
      "for": "passkey_name",
      label: "Name",
      validity: {
        valueMissing: "A passkey must have a name."
      },
      error: error?.fields.name?.message
    }, __h("input", {
      "class": input.input,
      id: "passkey_name",
      type: "name",
      name: "name",
      defaultValue: passkey ? passkeyName(passkey) : undefined,
      required: true
    })), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.editPasskey.loading
    }, "Update passkey"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      disabled: this.editPasskey.loading
    }, "Cancel"))));
  }
}

/**
 * @typedef {object} DeletePasskeyModalProps
 * @property {passkeyResponse | undefined} [passkey]
 * @property {() => void} onDelete
 * @property {() => void} onClose
 */

/** @extends Component<DeletePasskeyModalProps> */
class DeletePasskeyModal extends Component {
  passkey = signal(this.props.passkey);
  #stepUpRef = (/** @type {{ current: StepUp }} */createRef());
  deletePasskey = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */
  async ev => {
    ev.preventDefault();
    if (!this.props.passkey) return;
    await authedApiRequest("DELETE", `/v2/passkeys/${this.props.passkey.id}`, {
      json: {
        token: await this.#stepUpRef.current.requestToken("passkey_delete")
      }
    });
    this.props.onDelete();
  });

  /** @override */
  componentDidUpdate() {
    // setting this.props.passkey to undefined closes the modal,
    // but the modal is still briefly visible during its close animation
    // so we cache this.props.passkey to avoid visual bugs
    if (this.props.passkey) this.passkey.value = this.props.passkey;
  }
  render() {
    const {
      passkey,
      onClose
    } = this.props;
    const name = this.passkey.value ? passkeyName(this.passkey.value) : "",
      msg = name ? ["your passkey ", __h("span", {
        "class": util.textBold
      }, name)] : "your passkey";
    const error = this.deletePasskey.error && PasskeyError.from(this.deletePasskey.error);
    return __h(Modal, {
      title: "Remove passkey",
      open: passkey !== undefined,
      onClose: onClose
    }, __h("form", {
      id: "deletemodal-" + passkey?.id,
      method: "dialog"
    }), __h("form", {
      "class": css.form,
      onSubmit: this.deletePasskey.invoke
    }, __h("p", {}, "To remove ", msg, ", verify your identity."), __h(StepUp, {
      ref: this.#stepUpRef,
      error: error
    }), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      type: "submit",
      level: "primary",
      kind: "negative",
      loading: this.deletePasskey.loading
    }, "Remove passkey"), __h(Button, {
      type: "submit",
      form: "deletemodal-" + passkey?.id,
      disabled: this.deletePasskey.loading
    }, "Cancel"))));
  }
}